import { useReducer } from "react"
import useWindowSize from "./useWindowSize"
import { useSwipeable, SwipeableHandlers, EventData } from "react-swipeable"

const transitionTime = 400
const elastic = `transform ${transitionTime}ms cubic-bezier(0.68, -0.5, 1.55)`
const smooth = `transform ${transitionTime}ms ease`

interface CarouselState {
  offset: number
  desired: number
  initial: number
  active: number
}

const initialCarouselState: CarouselState = {
  offset: 0,
  desired: 0,
  initial: 0,
  active: 0,
}

interface CarouselNextAction {
  type: "next"
}

interface CarouselPrevAction {
  type: "prev"
}

interface CarouselJumpAction {
  type: "jump"
  active: number
  desired: number
}

interface CarouselDoneAction {
  type: "done"
}

interface CarouselDragAction {
  type: "drag"
  offset: number
}

type CarouselAction =
  | CarouselJumpAction
  | CarouselNextAction
  | CarouselPrevAction
  | CarouselDragAction
  | CarouselDoneAction

function carouselReducer(state: CarouselState, action: CarouselAction): CarouselState {
  switch (action.type) {
    case "jump":
      return {
        ...state,
        active: action.desired,
        desired: action.desired,
      }
    case "next":
      return {
        ...state,
        active: state.active,
        desired: state.active === 0 ? 0 : state.active + 1,
      }
    case "prev":
      return {
        ...state,
        active: state.desired,
        desired: state.active === 0 ? 0 : state.active - 1,
      }
    case "done":
      return {
        ...state,
        offset: NaN,
        active: state.desired,
      }
    case "drag":
      return {
        ...state,
        offset: action.offset,
      }
    default:
      return state
  }
}

function threshold(target: EventTarget) {
  const width = (target as HTMLElement).clientWidth
  return width / 3
}

function swiped(
  e: EventData,
  dispatch: React.Dispatch<CarouselAction>,
  active: number,
  offset: number,
  length: number,
  dir: 1 | -1,
) {
  const t = threshold(e.event.target)
  const d = dir * e.deltaX

  if (d >= t) {
    if (dir > 0) {
      dispatch({
        type: "jump",
        active: active < length - 1 ? active + 1 : length - 1,
        desired: active < length - 1 ? active + 1 : length - 1,
      })
    } else {
      dispatch({
        type: "jump",
        active: active <= 0 ? 0 : active - 1,
        desired: active <= 0 ? 0 : active - 1,
      })
    }
  } else {
    dispatch({
      type: "drag",
      offset: 0,
    })
  }
}

export default function useCarousel(
  length: number,
): [number, (n: number) => void, SwipeableHandlers, React.CSSProperties] {
  const window = useWindowSize()
  const [state, dispatch] = useReducer(carouselReducer, initialCarouselState)

  const handlers = useSwipeable({
    onSwiping(e) {
      dispatch({
        type: "drag",
        offset: -e.deltaX,
      })
    },
    onSwipedLeft(e) {
      if (window.width && window.width < 767) swiped(e, dispatch, state.active, state.offset, length, 1)
    },
    onSwipedRight(e) {
      if (window.width && window.width < 767) swiped(e, dispatch, state.active, state.offset, length, -1)
    },
    trackMouse: true,
    trackTouch: true,
  })

  const style: React.CSSProperties = {
    transform: "translateX(0)",
    width: `100%`,
  }

  if (state.desired !== state.initial) {
    style.transition = smooth
    style.transform = state.desired <= 0 && state.offset > 0 ? "translateX(0)" : `translateX(-${state.active * 100}%)`
  } else if (!isNaN(state.offset)) {
    if (state.offset !== 0) {
      style.transform = state.active > 0 ? `translateX(${state.offset}px)` : `translateX(${0}px)`
    } else {
      style.transition = elastic
    }
  }

  return [state.active, (n) => dispatch({ type: "jump", active: n, desired: n }), handlers, style]
}
